import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * The fixed action bar every onboarding step shares.
 *
 * Steps that are running rather than waiting on the user (Collect, Analysis)
 * don't render it at all — there is nothing to press, and a disabled Continue
 * just invites clicking.
 */

export const ONBOARDING_STEP_COUNT = 5;

export default function OnboardingFooter({
  step,
  note,
  continueLabel = 'Continue',
  continueDisabled = false,
  onContinue,
  onBack,
}: {
  /** 1-based position in the stepper, shown as "N / 5". */
  step: number;
  /** Left-aligned status line, e.g. "2 connected · 1 file uploaded". */
  note?: ReactNode;
  continueLabel?: string;
  continueDisabled?: boolean;
  onContinue: () => void;
  /** Omit on the first step, and wherever going back would be meaningless. */
  onBack?: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 animate-in border-t bg-card/80 fade-in backdrop-blur-sm duration-150">
      <div className="mx-auto flex max-w-[1180px] items-center justify-end gap-4 px-4 py-3 sm:px-8">
        {note && <p className="mr-auto text-xs text-muted-foreground">{note}</p>}
        <span className="text-xs tabular-nums text-muted-foreground">
          {step} / {ONBOARDING_STEP_COUNT}
        </span>
        {onBack && (
          <Button
            variant="outline"
            size="lg"
            className="cursor-pointer font-semibold tracking-tight"
            onClick={onBack}
          >
            <ArrowLeft className="size-4" />
            Back
          </Button>
        )}
        <Button
          size="lg"
          className="group cursor-pointer font-semibold tracking-tight"
          disabled={continueDisabled}
          onClick={onContinue}
        >
          {continueLabel}
          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    </div>
  );
}
